import { Icon } from './Icon'
import { ON_TINT_CLASS, TINT_CLASS } from '@/lib/tone'
import { cn } from '@/lib/cn'

/**
 * One representative glyph per hierarchy tier, in a tinted tile of that tier's
 * hue, so a level is recognisable by the same mark wherever you meet it.
 *
 * Shared by the Sites & Hierarchy tree and the place picker in the user editor.
 * It lives here because the second of those would have been a third copy: the
 * table on Sites & Hierarchy carried a comment calling itself "the same object
 * the scope menu in the top bar uses", and the two had already drifted. The top
 * bar still holds its own copy, because its rows style the tile differently;
 * when that stops being true it should come here too rather than a third being
 * written.
 *
 * Four tiers, since region and sub-region were dropped from the hierarchy —
 * their entries are removed rather than left as unreachable keys, because a
 * lookup table that answers for a tier nothing can be is how the drift this
 * repo keeps hitting gets to look harmless.
 *
 * Asset is the one tier with no hue: the ladder rations colour to the levels a
 * scope can be set to and that a person navigates, and an asset is the leaf. It
 * takes the neutral fill rather than borrowing a neighbour's.
 */
export const TIER_TONE = {
  enterprise: 'violet',
  site: 'green',
  line: 'ack',
  asset: null,
}

export const TIER_ICON = {
  enterprise: 'globe',
  site: 'factory',
  line: 'conveyor',
  asset: 'machine',
}

/**
 * @param {object} props
 * @param {string} props.tier
 * @param {boolean} [props.bare] The glyph alone, with no tile behind it — for a
 *                               breadcrumb, where a run of tiles reads as chips.
 */
export function TierGlyph({ tier, bare = false }) {
  const tone = TIER_TONE[tier]
  const glyph = (
    <Icon name={TIER_ICON[tier]} size="xs" className={tone ? ON_TINT_CLASS[tone] : 'text-ink-mute'} />
  )
  if (bare) return glyph
  return (
    <span
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-md',
        tone ? TINT_CLASS[tone] : 'bg-sunken',
      )}
    >
      {glyph}
    </span>
  )
}
